"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DriverCashLedgerService, {
  DriverCashLedger,
  DriverBalance,
  DriverCashWithdrawal,
  DebtReminderSettings,
  LedgerTransactionType,
  LedgerStatus,
} from "../services/driver-cash-ledger-service";
import { isSuccess } from "../../../models/response/base-response";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import { ApiConfig, API_USER_ROUTER, API_DRIVER_CASH_LEDGER_ROUTER } from "../../../services/api-config";
import BranchService, { Branch } from "../../branch/services/branch-service";
import { useFetchData } from "../../../hooks/useFetch";
import CookieUtils from "../../../utils/cookie-utils";
import { SelectDropdown } from "../../../components/custom/select-dropdown";
import ImagePreviewModal from "../../../components/custom/image-preview-modal";

// Convert yyyy-mm-dd (state) → dd/mm/yyyy (backend format + hiển thị trên DateInputCustom)
const toViDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return d && m && y ? `${d}/${m}/${y}` : "";
};

// Convert dd/mm/yyyy (DateInputCustom trả về) → yyyy-mm-dd (state)
const toYMD = (dmy: string): string => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return d && m && y ? `${y}-${m}-${d}` : "";
};

// Format currency 
const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const formatCurrency = (amount: number) => numberFmt.format(Number(amount) || 0);

// Format date (hỗ trợ cả ISO string và DD/MM/YYYY HH:mm)
const formatDate = (dateStr: string) => {
  if (!dateStr) return "—";
  // Thử parse DD/MM/YYYY HH:mm
  const viMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})\s*(\d{2})?:?(\d{2})?/);
  if (viMatch) {
    const [, d, m, y, h, min] = viMatch;
    return `${d}/${m}/${y}${h ? ` ${h}:${min || '00'}` : ''}`;
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
};

// Get transaction type text
const getTransactionTypeText = (type: LedgerTransactionType) => {
  switch (type) {
    case LedgerTransactionType.THU_TIEN_KHACH:
      return "Thu tiền khách hàng";
    case LedgerTransactionType.NOP_TIEN:
      return "Nộp tiền";
    case LedgerTransactionType.CHI_KHAC:
      return "Chi khác";
    default:
      return "Không xác định";
  }
};

// Get default dates (first and last day of current month)
const getDefaultDates = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const today = new Date();
  return {
    from: `${firstDay.getFullYear()}-${(firstDay.getMonth() + 1).toString().padStart(2, "0")}-${firstDay.getDate().toString().padStart(2, "0")}`,
    to: `${today.getFullYear()}-${(today.getMonth() + 1).toString().padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`,
  };
};

// Get status text & color
const getWithdrawalStatus = (status: LedgerStatus) => {
  switch (status) {
    case LedgerStatus.PENDING:
      return { text: "Chờ duyệt", color: "text-yellow-600", bg: "bg-yellow-50" };
    case LedgerStatus.APPROVED:
      return { text: "Đã duyệt", color: "text-green-600", bg: "bg-green-50" };
    case LedgerStatus.REJECTED:
      return { text: "Từ chối", color: "text-red-600", bg: "bg-red-50" };
    default:
      return { text: "?", color: "text-gray-600", bg: "bg-gray-50" };
  }
};

const DriverCashLedgerIndexPage: React.FC = () => {
  // Filters state
  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState(defaults.from);
  const [toDate, setToDate] = useState(defaults.to);
  const [driverId, setDriverId] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<number | "">("");

  // Data state
  const [ledgerEntries, setLedgerEntries] = useState<DriverCashLedger[]>([]);
  const [ledgerTotalRecord, setLedgerTotalRecord] = useState(0);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [serverTotalThu, setServerTotalThu] = useState(0);
  const [serverTotalChi, setServerTotalChi] = useState(0);

  // Driver balances state
  const [driverBalances, setDriverBalances] = useState<DriverBalance[]>([]);
  const [totalBalance, setTotalBalance] = useState(0);
  const [isLoadingBalances, setIsLoadingBalances] = useState(false);

  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState<DriverCashWithdrawal[]>([]);
  const [isLoadingWithdrawals, setIsLoadingWithdrawals] = useState(false);

  // Branch & employee lists
  const [branches, setBranches] = useState<Branch[]>([]);
  interface SimpleUser { id: number; name: string; }
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [employees, setEmployees] = useState<SimpleUser[]>([]);

  // Pagination
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize] = useState(20);

  // Image preview
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  // Withdrawal action modal
  const [actionModal, setActionModal] = useState<{
    type: "approve" | "reject";
    withdrawal: DriverCashWithdrawal;
  } | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<"ledger" | "balances" | "withdrawals">("ledger");

  // Debt reminder settings state
  const [debtSettings, setDebtSettings] = useState<DebtReminderSettings>({
    enabled: false,
    frequency_minutes: 60,
    min_debt_threshold: 0,
    start_hour: 8,
    end_hour: 20,
  });
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Services
  const { fetch: fetchLedger } = DriverCashLedgerService.list();
  const { fetch: fetchBalances } = DriverCashLedgerService.allBalances();
  const { fetch: fetchWithdrawals } = DriverCashLedgerService.withdrawals();
  const { fetch: fetchDebtSettings } = DriverCashLedgerService.getDebtReminderSettings();
  const { fetch: saveDebtSettings } = DriverCashLedgerService.updateDebtReminderSettings();
  const { fetch: listBranches } = BranchService.listActive();
  const { request: listUsers } = useFetchData.Get<any>({
    projectId: ApiConfig.PROJECT_ID.USER_SERVICE,
  });
  const { request: approveWithdrawal } = useFetchData.Post<any>({
    projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
  });
  const fetchLedgerRef = useRef(fetchLedger);
  const fetchBalancesRef = useRef(fetchBalances);
  const fetchWithdrawalsRef = useRef(fetchWithdrawals);
  const approveRef = useRef(approveWithdrawal);
  const fetchDebtSettingsRef = useRef(fetchDebtSettings);
  const saveDebtSettingsRef = useRef(saveDebtSettings);
  const branchRef = useRef(listBranches);
  const userRef = useRef(listUsers);

  useEffect(() => {
    fetchLedgerRef.current = fetchLedger;
  }, [fetchLedger]);

  useEffect(() => {
    fetchBalancesRef.current = fetchBalances;
  }, [fetchBalances]);

  useEffect(() => {
    fetchWithdrawalsRef.current = fetchWithdrawals;
  }, [fetchWithdrawals]);

  useEffect(() => {
    approveRef.current = approveWithdrawal;
  }, [approveWithdrawal]);

  useEffect(() => {
    fetchDebtSettingsRef.current = fetchDebtSettings;
  }, [fetchDebtSettings]);

  useEffect(() => {
    saveDebtSettingsRef.current = saveDebtSettings;
  }, [saveDebtSettings]);

  useEffect(() => { branchRef.current = listBranches; }, [listBranches]);
  useEffect(() => { userRef.current = listUsers; }, [listUsers]);

  // Load branches on mount
  useEffect(() => {
    (async () => {
      const res = await branchRef.current({ status: 1, company_id: 2000 });
      if (res && isSuccess(res)) {
        const list = Array.isArray(res.data) ? res.data : (res.data as any)?.list ?? (res.data as any)?.data ?? [];
        setBranches(list);
      }
    })();
  }, []);

  // Load all employees on mount
  useEffect(() => {
    (async () => {
      const res = await userRef.current({
        endPoint: API_USER_ROUTER.API_LIST,
        data: { limit: 500, status: 1, company_id: 2000 },
      });
      if (res && isSuccess(res)) {
        const list = Array.isArray(res.data) ? res.data : (res.data as any)?.list ?? (res.data as any)?.data ?? [];
        setAllUsers(list);
      }
    })();
  }, []);

  // Filter employees by selected branch
  useEffect(() => {
    if (!selectedBranchId) {
      setEmployees(allUsers.map((u: any) => ({ id: u.id, name: u.name })));
    } else {
      const filtered = allUsers.filter((u: any) =>
        u.branches?.some((b: any) => b.id === selectedBranchId) || u.is_all_branch === 1
      );
      setEmployees(filtered.map((u: any) => ({ id: u.id, name: u.name })));
    }
    setDriverId("");
  }, [selectedBranchId, allUsers]);

  // Load debt reminder settings
  const loadDebtSettings = useCallback(async () => {
    setIsLoadingSettings(true);
    try {
      const res = await fetchDebtSettingsRef.current();
      if (res && isSuccess(res) && res.data) {
        setDebtSettings(res.data);
      }
    } catch (error) {
      console.error("Failed to load debt reminder settings:", error);
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  // Save debt reminder settings
  const handleSaveDebtSettings = useCallback(async () => {
    setIsSavingSettings(true);
    try {
      const res = await saveDebtSettingsRef.current(debtSettings);
      if (res && isSuccess(res)) {
        alert("Lưu cấu hình nhắc nợ thành công!");
      } else {
        alert("Lưu cấu hình thất bại!");
      }
    } catch (error) {
      console.error("Failed to save debt reminder settings:", error);
      alert("Lưu cấu hình thất bại!");
    } finally {
      setIsSavingSettings(false);
    }
  }, [debtSettings]);

  // Handle approve/reject withdrawal via modal
  const handleWithdrawalAction = useCallback(async () => {
    if (!actionModal) return;
    setActionLoading(true);
    try {
      const { type, withdrawal } = actionModal;
      const data: any = { status: type === "approve" ? 2 : 3 };
      if (type === "reject" && rejectReason) data.reject_reason = rejectReason;

      const res = await approveRef.current({
        endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_APPROVE_WITHDRAWAL(withdrawal.id),
        data,
      });
      if (res && isSuccess(res)) {
        setActionModal(null);
        setRejectReason("");
        void loadWithdrawals();
        if (type === "approve") void loadBalances();
      }
    } catch {
      // error handled by hook
    } finally {
      setActionLoading(false);
    }
  }, [actionModal, rejectReason]);

  // Load ledger data
  const loadLedger = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page: ledgerPage, limit: ledgerPageSize };
      if (fromDate) params.from_date = toViDate(fromDate);
      if (toDate) params.to_date = toViDate(toDate);
      if (driverId) params.driver_id = Number(driverId);
      if (selectedBranchId) params.branch_id = selectedBranchId;

      const res = await fetchLedgerRef.current(params);
      if (res && isSuccess(res)) {
        const entries = res.data?.list ?? [];
        setLedgerEntries(entries);
        setLedgerTotalRecord(res.data?.total_record ?? 0);
        setServerTotalThu(Number(res.data?.total_collected ?? res.data?.total_thu ?? 0));
        setServerTotalChi(Number(res.data?.total_deposited ?? res.data?.total_nop ?? 0));
        if (entries.length > 0) {
          const sorted = [...entries].sort((a, b) =>
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime() || a.id - b.id
          );
          setOpeningBalance(sorted[0]?.balance_before ?? 0);
        } else {
          setOpeningBalance(0);
        }
      }
    } catch (error) {
      console.error("Failed to load ledger:", error);
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, driverId, selectedBranchId, ledgerPage, ledgerPageSize]);

  // Load driver balances
  const loadBalances = useCallback(async () => {
    setIsLoadingBalances(true);
    try {
      const res = await fetchBalancesRef.current();
      if (res && isSuccess(res)) {
        const data = res.data;
        setDriverBalances(data?.list ?? []);
        setTotalBalance(Number(data?.total_balance ?? 0));
      }
    } catch (error) {
      console.error("Failed to load balances:", error);
    } finally {
      setIsLoadingBalances(false);
    }
  }, []);

  // Load withdrawals
  const loadWithdrawals = useCallback(async () => {
    setIsLoadingWithdrawals(true);
    try {
      const params: any = { page: 1, limit: 100 };
      if (fromDate) params.from_date = toViDate(fromDate);
      if (toDate) params.to_date = toViDate(toDate);
      if (driverId) params.driver_id = Number(driverId);
      if (selectedBranchId) params.branch_id = selectedBranchId;

      const res = await fetchWithdrawalsRef.current(params);
      if (res && isSuccess(res)) {
        setWithdrawals(res.data?.list ?? []);
      }
    } catch (error) {
      console.error("Failed to load withdrawals:", error);
    } finally {
      setIsLoadingWithdrawals(false);
    }
  }, [fromDate, toDate, driverId, selectedBranchId]);

  // Initial load + debt settings
  useEffect(() => {
    void loadDebtSettings();
  }, []);

  // Auto-search when filters change
  useEffect(() => {
    if (activeTab === "ledger") {
      void loadLedger();
    } else if (activeTab === "withdrawals") {
      void loadWithdrawals();
    } else {
      void loadBalances();
    }
  }, [fromDate, toDate, driverId, selectedBranchId, activeTab]);

  // Calculate running balance
  const getRunningBalance = (entries: DriverCashLedger[]) => {
    // Cùng created_at (duyệt nộp hàng loạt cùng giây) thì xếp theo id — dòng tạo sau mang số dư cuối
    const sorted = [...entries].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime() || b.id - a.id
    );
    return sorted.map((entry) => ({
      ...entry,
      runningBalance: entry.balance_after,
    }));
  };

  const entriesWithBalance = getRunningBalance(ledgerEntries);

  // Reset page when filters change (not when page changes)
  useEffect(() => { setLedgerPage(1); }, [fromDate, toDate, driverId, selectedBranchId]);

  // Server-side pagination
  const ledgerTotalPages = Math.max(1, Math.ceil(ledgerTotalRecord / ledgerPageSize));

  // Ledger totals from server
  const ledgerTotals = useMemo(() => {
    const closingBalance = entriesWithBalance.length > 0 ? entriesWithBalance[0].runningBalance : 0;
    return { totalThu: serverTotalThu, totalChi: serverTotalChi, closingBalance };
  }, [serverTotalThu, serverTotalChi, entriesWithBalance]);

  const formatHeaderDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate().toString().padStart(2, "0")}-${(d.getMonth() + 1).toString().padStart(2, "0")}-${d.getFullYear()}`;
  };

  const getImageUrl = (path: string) => {
    const resourceUrl = CookieUtils.getCookie("CONFIG_RESOURCE_URL") || '';
    return `${resourceUrl}/${path}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sổ quỹ nhân viên</h1>
              <p className="text-sm text-gray-500 mt-0.5">Quản lý thu chi và công nợ nhân viên</p>
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {([
              { key: "ledger" as const, label: "Sổ quỹ" },
              { key: "balances" as const, label: "Dư nợ NV" },
              { key: "withdrawals" as const, label: "Yêu cầu nộp tiền" },
            ]).map(tab => (
              <button
                key={tab.key}
                onClick={() => {
                  setActiveTab(tab.key);
                  if (tab.key === "balances") void loadBalances();
                  if (tab.key === "withdrawals") void loadWithdrawals();
                }}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                  activeTab === tab.key
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Filters */}
        {activeTab !== "balances" && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Từ ngày</label>
                <DateInputCustom
                  type="date"
                  value={toViDate(fromDate)}
                  onChange={(d) => setFromDate(toYMD(d ?? ""))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Đến ngày</label>
                <DateInputCustom
                  type="date"
                  value={toViDate(toDate)}
                  onChange={(d) => setToDate(toYMD(d ?? ""))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Chi nhánh</label>
                <select value={selectedBranchId} onChange={(e) => setSelectedBranchId(e.target.value ? Number(e.target.value) : "")}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none bg-white transition-colors">
                  <option value="">Tất cả chi nhánh</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Nhân viên</label>
                <SelectDropdown
                  placeholder="Tất cả nhân viên"
                  hasAllOption={true}
                  labelAllOption="Tất cả nhân viên"
                  dataOptions={employees}
                  getOptionLabel={(option: any) => option.name}
                  getOptionKey={(option: any) => option.id}
                  selectedId={driverId ? Number(driverId) : -1}
                  onChange={(selected: any) => {
                    setDriverId(selected && selected.id !== -1 ? selected.id.toString() : "");
                  }}
                  className="w-full rounded-xl outline-none bg-white transition-colors border-0 p-0"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ======================== TAB: SỔ QUỸ ======================== */}
      {activeTab === "ledger" && (
        <>
          {/* Summary cards */}
          {entriesWithBalance.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <p className="text-sm font-medium text-gray-500">Tồn đầu kỳ</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(openingBalance)}đ</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <p className="text-sm font-medium text-gray-500">Tổng thu</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">+{formatCurrency(ledgerTotals.totalThu)}đ</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <p className="text-sm font-medium text-gray-500">Tổng nộp</p>
                <p className="text-2xl font-bold text-red-500 mt-1">-{formatCurrency(ledgerTotals.totalChi)}đ</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <p className="text-sm font-medium text-gray-500">Tồn cuối kỳ</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">{formatCurrency(ledgerTotals.closingBalance)}đ</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Chi tiết giao dịch</h2>
              <span className="text-sm text-gray-400">{ledgerTotalRecord} giao dịch</span>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12">#</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Ngày</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Diễn giải</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Loại</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Nhân viên</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Khách hàng</th>
                  <th className="px-4 py-3 text-right font-semibold text-emerald-600">Thu</th>
                  <th className="px-4 py-3 text-right font-semibold text-red-500">Chi</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Tồn</th>
                </tr>
                {/* Tổng cộng ở trên */}
                {entriesWithBalance.length > 0 && (
                  <tr className="bg-emerald-50/60 border-b-2 border-emerald-200 font-bold">
                    <td colSpan={6} className="px-4 py-2.5 text-right text-emerald-800 text-xs uppercase tracking-wide">Tổng cộng</td>
                    <td className="px-4 py-2.5 text-right text-emerald-700 font-bold">+{formatCurrency(ledgerTotals.totalThu)}</td>
                    <td className="px-4 py-2.5 text-right text-red-600 font-bold">-{formatCurrency(ledgerTotals.totalChi)}</td>
                    <td className="px-4 py-2.5 text-right text-gray-900 font-extrabold">{formatCurrency(ledgerTotals.closingBalance)}</td>
                  </tr>
                )}
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <svg className="animate-spin w-6 h-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-400 text-sm">Đang tải...</span>
                    </div>
                  </td></tr>
                ) : entriesWithBalance.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">Không có dữ liệu</td></tr>
                ) : (
                  entriesWithBalance.map((entry, i) => {
                    const isIncome = entry.transaction_type === LedgerTransactionType.THU_TIEN_KHACH;
                    const day = new Date(entry.created_at).getDate().toString().padStart(2, "0");
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3 text-center text-gray-400">{(ledgerPage - 1) * ledgerPageSize + i + 1}</td>
                        <td className="px-4 py-3 text-center align-top text-gray-500">{day}</td>
                        <td className="px-4 py-3 align-top">
                          <div className="font-medium text-gray-900">{entry.driver_name || `NV #${entry.driver_id}`}</div>
                          <div className="text-gray-400 text-xs">#{entry.reference_id || entry.id}</div>
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="text-gray-700">{getTransactionTypeText(entry.transaction_type)}</div>
                          <div className="text-gray-400 text-xs">{formatDate(entry.created_at)}</div>
                        </td>
                        <td className="px-4 py-3 text-center align-top">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700">
                            {entry.driver_name?.split(" ").pop() || `NV${entry.driver_id}`}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-top text-gray-600">
                          {entry.order_code ? <span>{entry.order_code}</span> : <span className="text-gray-300">-</span>}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-emerald-600 font-semibold">
                          {isIncome ? `+${formatCurrency(entry.amount)}` : ""}
                        </td>
                        <td className="px-4 py-3 text-right align-top text-red-500 font-semibold">
                          {!isIncome ? `-${formatCurrency(Math.abs(entry.amount))}` : ""}
                        </td>
                        <td className="px-4 py-3 text-right align-top font-bold text-gray-900">
                          {formatCurrency(entry.runningBalance)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            {ledgerTotalRecord > ledgerPageSize && (
              <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Hiển thị {(ledgerPage - 1) * ledgerPageSize + 1}-{Math.min(ledgerPage * ledgerPageSize, ledgerTotalRecord)} / {ledgerTotalRecord}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => setLedgerPage(1)} disabled={ledgerPage === 1}
                    className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">«</button>
                  <button onClick={() => setLedgerPage(p => Math.max(1, p - 1))} disabled={ledgerPage === 1}
                    className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">‹</button>
                  {Array.from({ length: ledgerTotalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === ledgerTotalPages || Math.abs(p - ledgerPage) <= 2)
                    .reduce<(number | string)[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("...");
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      typeof p === "string" ? (
                        <span key={`dot-${idx}`} className="px-1 text-gray-400">…</span>
                      ) : (
                        <button key={p} onClick={() => setLedgerPage(p)}
                          className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                            p === ledgerPage ? "bg-emerald-500 text-white border-emerald-500" : "border-gray-200 hover:bg-gray-50"
                          }`}>{p}</button>
                      )
                    )}
                  <button onClick={() => setLedgerPage(p => Math.min(ledgerTotalPages, p + 1))} disabled={ledgerPage === ledgerTotalPages}
                    className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">›</button>
                  <button onClick={() => setLedgerPage(ledgerTotalPages)} disabled={ledgerPage === ledgerTotalPages}
                    className="px-2.5 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">»</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ======================== TAB: DU NO NV ======================== */}
      {activeTab === "balances" && (
        <div className="space-y-6">
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <p className="text-sm font-medium text-gray-500">Tổng dư nợ tất cả NV</p>
              <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalBalance)}đ</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <p className="text-sm font-medium text-gray-500">NV đang giữ tiền</p>
              <p className="text-2xl font-bold text-blue-600 mt-1">{driverBalances.filter(d => Number(d.current_balance) > 0).length}</p>
            </div>
            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <p className="text-sm font-medium text-gray-500">Tổng số NV</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">{driverBalances.length}</p>
            </div>
          </div>

          {/* Debt Reminder Settings */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Cấu hình nhắc nợ tự động</h3>
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={debtSettings.enabled}
                    onChange={(e) => setDebtSettings({ ...debtSettings, enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
                <span className={`text-sm font-medium ${debtSettings.enabled ? "text-green-600" : "text-gray-400"}`}>
                  {debtSettings.enabled ? "Đang bật" : "Đã tắt"}
                </span>
              </div>
            </div>
            {isLoadingSettings ? (
              <div className="text-center text-gray-500 py-4">Đang tải cấu hình...</div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Tần suất nhắc</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={1}
                        value={debtSettings.frequency_minutes || ''}
                        onChange={(e) => setDebtSettings({ ...debtSettings, frequency_minutes: e.target.value === '' ? 0 : Number(e.target.value) })}
                        onBlur={() => { if (!debtSettings.frequency_minutes || debtSettings.frequency_minutes < 1) setDebtSettings({ ...debtSettings, frequency_minutes: 1 }); }}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      />
                      <span className="text-sm text-gray-500 whitespace-nowrap">phút</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Ngưỡng nợ tối thiểu</label>
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        min={0}
                        step={10000}
                        value={debtSettings.min_debt_threshold}
                        onChange={(e) => setDebtSettings({ ...debtSettings, min_debt_threshold: Number(e.target.value) || 0 })}
                        className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                      />
                      <span className="text-sm text-gray-500">đ</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Giờ bắt đầu</label>
                    <select
                      value={debtSettings.start_hour}
                      onChange={(e) => setDebtSettings({ ...debtSettings, start_hour: Number(e.target.value) })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1.5">Giờ kết thúc</label>
                    <select
                      value={debtSettings.end_hour}
                      onChange={(e) => setDebtSettings({ ...debtSettings, end_hour: Number(e.target.value) })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition-colors"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSaveDebtSettings}
                    disabled={isSavingSettings}
                    className="bg-blue-500 text-white px-6 py-2 rounded-xl text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {isSavingSettings ? "Đang lưu..." : "Lưu cấu hình"}
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Balance table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Tổng hợp dư nợ nhân viên</h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Nhân viên</th>
                  <th className="px-4 py-3 text-right font-semibold text-emerald-600">Đã thu</th>
                  <th className="px-4 py-3 text-right font-semibold text-blue-600">Đã nộp</th>
                  <th className="px-4 py-3 text-right font-semibold text-red-500">Dư nợ</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Số GD</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">GD gần nhất</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoadingBalances ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">Đang tải...</td></tr>
                ) : driverBalances.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-400">Không có dữ liệu</td></tr>
                ) : (
                  driverBalances.map((driver, index) => {
                    const balance = Number(driver.current_balance) || 0;
                    const collected = Number(driver.total_collected) || 0;
                    const deposited = Number(driver.total_deposited) || 0;
                    return (
                      <tr key={driver.driver_id} className={`hover:bg-gray-50/50 transition-colors ${balance > 0 ? "bg-red-50/30" : ""}`}>
                        <td className="px-4 py-3 text-center text-gray-400">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium">{driver.driver_name || `NV #${driver.driver_id}`}</div>
                          {driver.driver_phone && (
                            <div className="text-xs text-gray-500">{driver.driver_phone}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-emerald-600 font-medium">
                          {formatCurrency(collected)}
                        </td>
                        <td className="px-4 py-3 text-right text-blue-600 font-medium">
                          {formatCurrency(deposited)}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${balance > 0 ? "text-red-600" : "text-emerald-600"}`}>
                          {formatCurrency(balance)}đ
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">{driver.transaction_count}</span>
                        </td>
                        <td className="px-4 py-3 text-center text-xs text-gray-500">
                          {driver.last_transaction_at ? formatDate(driver.last_transaction_at) : "-"}
                        </td>
                      </tr>
                    );
                  })
                )}
                {/* Total row */}
                {driverBalances.length > 0 && (
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                    <td colSpan={2} className="px-4 py-3 text-right text-gray-700">Tổng cộng</td>
                    <td className="px-4 py-3 text-right text-emerald-600">
                      {formatCurrency(driverBalances.reduce((s, d) => s + (Number(d.total_collected) || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-600">
                      {formatCurrency(driverBalances.reduce((s, d) => s + (Number(d.total_deposited) || 0), 0))}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600">
                      {formatCurrency(totalBalance)}đ
                    </td>
                    <td colSpan={2} className="px-4 py-3"></td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================== TAB: YÊU CẦU NỘP TIỀN ======================== */}
      {activeTab === "withdrawals" && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Yêu cầu nộp tiền</h2>
            <span className="text-sm text-gray-400">{withdrawals.length} yêu cầu</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="px-4 py-3 text-center font-semibold text-gray-600 w-12">#</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600 min-w-[140px]">Nhân viên</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Số tiền</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Trạng thái</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Hình CK</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Ghi chú</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600">Thời gian</th>
                  <th className="px-4 py-3 text-center font-semibold text-gray-600 min-w-[140px]">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {isLoadingWithdrawals ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="flex flex-col items-center gap-2">
                        <svg className="animate-spin w-6 h-6 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-gray-400 text-sm">Đang tải...</span>
                      </div>
                    </td>
                  </tr>
                ) : withdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-400">Không có yêu cầu nộp tiền</td>
                  </tr>
                ) : (
                  withdrawals.map((w, index) => {
                    const statusInfo = getWithdrawalStatus(w.status);
                    const imageUrl = w.transfer_image ? getImageUrl(w.transfer_image) : null;
                    return (
                      <tr key={w.id} className={`hover:bg-gray-50/50 transition-colors ${w.status === LedgerStatus.PENDING ? "bg-orange-50/30" : ""}`}>
                        <td className="px-4 py-3 text-center text-gray-400">{index + 1}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{w.driver_name || `NV #${w.driver_id}`}</div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="font-bold text-gray-900">{formatCurrency(w.amount)}</span>
                          <span className="text-gray-400 text-xs ml-0.5">đ</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusInfo.color} ${statusInfo.bg}`}>
                            {statusInfo.text}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt="CK"
                              className="w-12 h-12 object-cover rounded-lg cursor-pointer inline-block border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all"
                              onClick={() => setPreviewImage(imageUrl)}
                            />
                          ) : (
                            <span className="text-gray-300 text-xs">Không có</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600 max-w-[200px]">
                          <div className="text-sm">{w.note || "—"}</div>
                          {w.reject_reason && (
                            <div className="text-red-500 text-xs mt-1 bg-red-50 rounded px-2 py-0.5 inline-block">
                              Lý do từ chối: {w.reject_reason}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="text-xs text-gray-500">{formatDate(w.created_at)}</div>
                          {w.approved_by_name && (
                            <div className="text-xs text-gray-400 mt-0.5">Bởi: {w.approved_by_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {w.status === LedgerStatus.PENDING ? (
                            <div className="flex gap-2 justify-center">
                              <button
                                onClick={() => setActionModal({ type: "approve", withdrawal: w })}
                                className="inline-flex items-center gap-1 bg-emerald-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-emerald-600 transition-colors shadow-sm"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Duyệt
                              </button>
                              <button
                                onClick={() => { setRejectReason(""); setActionModal({ type: "reject", withdrawal: w }); }}
                                className="inline-flex items-center gap-1 bg-red-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-red-600 transition-colors shadow-sm"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                Từ chối
                              </button>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">Đã xử lý</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Withdrawal Action Modal */}
      {actionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !actionLoading && setActionModal(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
            <div className="text-center mb-4">
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center ${actionModal.type === "approve" ? "bg-green-100" : "bg-red-100"}`}>
                <svg className={`w-7 h-7 ${actionModal.type === "approve" ? "text-green-600" : "text-red-600"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {actionModal.type === "approve" ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  )}
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mt-3">
                {actionModal.type === "approve" ? "Duyệt yêu cầu nộp tiền" : "Từ chối yêu cầu nộp tiền"}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {actionModal.withdrawal.driver_name || `NV #${actionModal.withdrawal.driver_id}`}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 text-center mb-4">
              <p className="text-xs text-gray-500">Số tiền</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(actionModal.withdrawal.amount)}đ</p>
            </div>

            {actionModal.type === "approve" && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
                <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Lưu ý</p>
                  <p className="mt-0.5">Khi duyệt, hệ thống sẽ tự động tạo <strong>phiếu thu</strong> tương ứng trong mục Quản lý Phiếu Thu Chi.</p>
                </div>
              </div>
            )}

            {actionModal.type === "reject" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Lý do từ chối</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none resize-none"
                  rows={3}
                  placeholder="Nhập lý do từ chối..."
                  autoFocus
                />
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setActionModal(null)}
                disabled={actionLoading}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Hủy
              </button>
              <button
                onClick={handleWithdrawalAction}
                disabled={actionLoading || (actionModal.type === "reject" && !rejectReason.trim())}
                className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50 transition-colors ${
                  actionModal.type === "approve"
                    ? "bg-green-500 hover:bg-green-600"
                    : "bg-red-500 hover:bg-red-600"
                }`}
              >
                {actionLoading ? "Đang xử lý..." : actionModal.type === "approve" ? "Xác nhận duyệt" : "Xác nhận từ chối"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      <ImagePreviewModal
        opened={!!previewImage}
        src={previewImage || ""}
        alt="Hình chuyển khoản"
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default DriverCashLedgerIndexPage;
